/* eslint-disable react/self-closing-comp */
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Post, PostDetailComponent } from '../../components';
import { AsideRight } from '../../views/aside-right';
import { usePostHook } from '../../hooks/post/usePost';
import _size from 'lodash/size';
import CreatePost from '../../components/create-post/create-post.component';
import { IPostModel } from '../../models';
import { useHashTag } from '../../hooks/hashtag/useHashtag';
import Loading from '../../components/loading/loading.component';
import { useSelector } from 'react-redux';
import { RootState, LocalStorageService } from '../../configuration';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useCategories } from '../../hooks/use-categories/useCategories';
import toast from 'react-hot-toast';
import { Advertisement } from '../profile/components/Advertisement';
import { handleFormatHastag } from '../../utils/formatHastag';
import NativeAdCard from '../../components/ads/native-ad-card';
import { isAdSafeContent } from '../../utils/ad-brand-safety';
import LiveDonateTicker from '../../components/live-donate/live-donate-ticker';
import LiveDonateFeed from '../../components/live-donate/live-donate-feed';
import DonateEventStrip from '../../components/donate-event/donate-event-strip';

export const HomePage = () => {
  const [pageNumber, setPageNumber] = useState<number>(1);
  const profileUser = useSelector(
    (state: RootState) => state.features.auth.profile
  );
  // Đã đăng nhập hay chưa: đọc thẳng token trong localStorage (như root.component.tsx) thay vì
  // chờ `profileUser` từ Redux — `profileUser` chỉ có sau khi fetchProfile() (async) chạy xong,
  // nên nếu dùng nó để quyết định bố cục thì người đã đăng nhập sẽ thấy bố cục khách vãng lai
  // trong tích tắc lúc tải trang rồi mới nhảy sang bố cục người dùng (mở/đóng kết nối SignalR 2 lần).
  const isLoggedIn = !!LocalStorageService.getItem('accessToken');
  const { getPostCategory } = useCategories({ type: 'post' });
  const navigate = useNavigate();
  const {
    data: listPost,
    hasMore,
    loading,
    appendNewPost,
  } = usePostHook({ pageNumber });
  const { hashtags, getHashTags } = useHashTag();
  const { id: postId } = useParams();
  const navigator = useNavigate();
  return (
    <div className="lg:flex 2xl:gap-16 2xl:px-16 gap-6 pt-2 px-6 mx-auto">
      <Helmet>
        <title>Cab VN</title>
        <meta name="home" content="Cab VN" />
      </Helmet>
      {postId ? <PostDetailComponent postId={postId} /> : null}
      {/* Cột trái CHỈ chiếm chỗ khi đã đăng nhập — chưa login thì bỏ hẳn để khỏi để lại khoảng
          trống 369px. Khách vãng lai vẫn thấy khung Donate trực tiếp, nhưng ở cột phải sẵn có của
          họ (hashtag/quảng cáo, phía dưới) thay vì tạo thêm một cột trái riêng. */}
      {isLoggedIn && (
        <div className="w-full max-w-[300px] lg:w-[300px] lg:shrink-0">
          <AsideRight hashtags={hashtags || []} showLiveDonate />
        </div>
      )}
      {/* Đăng nhập: feed co giãn giữa 2 cột (flex-1). Chưa login: không có cột trái → cho feed rộng
          tối đa 820px + căn giữa để bài viết/nametag/avatar không bị nhỏ và không lệch trống bên trái. */}
      <div
        className={`mx-auto pb-4 ${
          isLoggedIn ? 'flex-1' : 'w-full max-w-[820px]'
        }`}
      >
        <main className="w-full">
          <div className="mb-[10px] w-full">
            {/* Dải sự kiện thử thách donate — một vị trí duy nhất, hiện ở mọi kích thước màn hình
                (không tách máy tính/điện thoại như dải Donate trực tiếp bên dưới). */}
            <DonateEventStrip />
            {/* Người đăng nhập: khung donate ở AsideRight hiện từ `lg` (1024px) → dải ẩn cùng
                ngưỡng. Khách vãng lai: khung donate chuyển xuống cột phải hashtag/quảng cáo, cột
                đó chỉ hiện từ `xl` (1280px) → dải phải ẩn muộn hơn, ở `xl`, không phải `lg`. */}
            <LiveDonateTicker
              desktopMinWidth={isLoggedIn ? 1024 : 1280}
              hiddenClass={isLoggedIn ? 'lg:hidden' : 'xl:hidden'}
            />
            <CreatePost
              profileUser={!!profileUser}
              onCreatedSuccess={async (newPost: IPostModel) => {
                toast.success(
                  'Đăng bài thành công !. Bài viết của bạn sẽ được duyệt trước khi hiển thị.',
                  {
                    duration: 6000,
                  }
                );
                navigate('/post-uncheck');
                await getHashTags();
              }}
              isGroup={false}
            />
          </div>

          {loading ? <Loading classname="mx-auto" /> : null}

          {_size(listPost) ? (
            <InfiniteScroll
              dataLength={listPost.length}
              next={() => {
                setPageNumber((prev) => prev + 1);
              }}
              hasMore={hasMore}
              loader={<Loading classname="mx-auto" />}
              endMessage={null}
              scrollableTarget="body"
              className="flex-1 xl:space-y-[10px] space-y-[10px]"
            >
              {listPost?.map((post, index) => {
                return (
                  <React.Fragment key={post.id}>
                    <Post
                      getPostCategory={getPostCategory}
                      post={post}
                      isPostUser={post.userId === profileUser?.userId}
                    />
                    {/* Chèn 1 quảng cáo Native sau bài thứ 3, rồi cứ mỗi 6 bài — BỎ QUA nếu bài
                        nhạy cảm/tiêu cực (brand safety: không đặt quảng cáo cạnh nội dung xấu). */}
                    {(index === 2 || (index > 2 && (index - 2) % 6 === 0)) && isAdSafeContent(post) ? (
                      <NativeAdCard categoryId={post.categoryId} />
                    ) : null}
                  </React.Fragment>
                );
              })}
            </InfiniteScroll>
          ) : null}
        </main>
      </div>
      <div className="flex lg:sticky flex-col gap-4 xl:block md:hidden top-[84px] lg:h-[calc(100vh-64px)] overflow-y-auto  pb-10 xl:w-[369px] shrink-0">
        {/* Cột phải này chỉ thật sự hiện ra từ `xl` (1280px) trở lên — khớp với `desktopMinWidth`
            truyền cho LiveDonateFeed bên dưới. Chỉ khách vãng lai cần khung này ở đây; người đã
            đăng nhập đã có khung donate riêng ở AsideRight (cột trái). */}
        {!isLoggedIn && <LiveDonateFeed desktopMinWidth={1280} />}
        <Advertisement />

        {hashtags && (
          <section>
            <div className="box border mt-4 border-[#DEE0E6] dark:border-slate-700 bg-[#D9D9D9] rounded-xl dark:bg-dark2">
              <div className="flex justify-between h-[53px] px-4 py-[18px] relative text-black ">
                <div className=" absolute inset-0 ">
                  <svg
                    width="201"
                    height="53"
                    viewBox="0 0 201 53"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M0 12C0 5.37258 5.37258 0 12 0H153V53H0V12Z"
                      className="fill-white dark:fill-slate-800"
                    />
                    <path
                      d="M153 0C156.57 0 159.844 1.98058 161.502 5.14192L175.465 31.7732C180.81 41.9673 190.001 49.6077 201 53H153V0Z"
                      className="fill-white dark:fill-slate-800"
                    />
                  </svg>
                </div>
                <span className="font-bold relative text-[#1A1D29] dark:text-white text-sm">
                  Hashtag phổ biến
                </span>
              </div>

              <div className="w-full px-4 py-5 bg-white dark:bg-dark2 rounded-xl rounded-tl-none">
                <div className="space-y-3.5 px-8 capitalize text-xs font-normal text-gray-600 dark:text-slate-400 ">
                  {hashtags.map((hashtag, index) => {
                    return (
                      <div
                        key={index}
                        onClick={() =>
                          navigator(
                            `hashtag/${handleFormatHastag(hashtag?.name)}`
                          )
                        }
                        className="flex flex-col cursor-pointer gap-1 p-3 shadow-[0px_8px_16px_0px_rgba(0,0,0,0.04),0px_2px_12px_0px_rgba(0,0,0,0.04)] dark:shadow-none dark:bg-slate-900 rounded-lg"
                      >
                        <div className="flex gap-1 items-center">
                          <svg
                            width="17"
                            height="16"
                            viewBox="0 0 17 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M10.5161 5.04437L13.2776 2.66487C13.349 2.60378 13.3997 2.52278 13.4227 2.43274C13.4457 2.3427 13.44 2.24793 13.4063 2.16115C13.3762 2.0724 13.3186 1.99504 13.2414 1.9398C13.1643 1.88456 13.0715 1.85419 12.9759 1.8529H4.77788V1.48921C4.77788 1.36858 4.72889 1.25289 4.64167 1.16759C4.55446 1.08228 4.43617 1.03436 4.31283 1.03436C4.18949 1.03436 4.0712 1.08228 3.98399 1.16759C3.89677 1.25289 3.84778 1.36858 3.84778 1.48921V14.1977H4.77788V8.22079H12.9759C13.0693 8.21944 13.1602 8.19068 13.2368 8.13824C13.3134 8.0858 13.3721 8.01209 13.4054 7.92664C13.4357 7.83946 13.4396 7.74558 13.4168 7.65625C13.3939 7.56693 13.3453 7.48592 13.2766 7.42293L10.5161 5.04437Z"
                              fill="#DC2626"
                            />
                          </svg>
                          <span className="text-[#6B7280] text-xs">
                            Hashtag
                          </span>
                        </div>
                        <span className="font-semibold text-black dark:text-white  text-sm">
                          # {handleFormatHastag(hashtag?.name)}
                        </span>
                        <div className="mt-0.5">
                          {hashtag.point.toFixed(2)} point{' '}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Policy Links Section */}
        <section className="mt-4">
          <div className="box border border-[#DEE0E6] dark:border-slate-700 bg-white rounded-xl dark:bg-dark2 p-4">
            <div className="text-xs text-gray-600 dark:text-gray-400 text-center leading-relaxed">
              <div className="flex flex-wrap justify-center items-center gap-1">
                <a
                  href="/privacy_and_complaint.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-blue-600 dark:hover:text-blue-400"
                >
                  Chính sách & Khiếu nại
                </a>
                <span className="text-gray-400">•</span>
                <a
                  href="/operating_regulations_and_term_of_service.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-blue-600 dark:hover:text-blue-400"
                >
                  Điều khoản & Quy chế
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Copyright Footer */}
        <section className="mt-4">
          <div className="box border border-[#DEE0E6] dark:border-slate-700 bg-white rounded-xl dark:bg-dark2 p-4">
            <div className="text-xs text-gray-500 dark:text-gray-400 text-center leading-relaxed">
              <p className="mb-2 font-semibold text-gray-700 dark:text-gray-300">
                © 2025 Cab.vn - All Rights Reserved
              </p>
              <p className="mb-1 text-xs">
                Giấy xác nhận thông báo cung cấp dịch vụ mạng xã hội số:
                6206/GXN-SVHTT
              </p>
              <p className="text-xs">
                Do Sở Văn hóa và Thể thao Thành phố Hồ Chí Minh cấp ngày
                24/11/2025
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
